<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <Maintenance v-if="isMaintenance" />
    <component v-else :is="layout">
      <router-view />
    </component>
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch, computed } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";
import Maintenance from "./views/error/Maintenance.vue";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    Maintenance,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
    appVersion() {
      const full = this.$store.state.app.appVersion.split('.');

      return {
        major: Number(full[0]),
        minor: Number(full[1]),
        patch: Number(full[2]),
      }
    }
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // Helper function to convert hex to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : null;
    };

    Object.entries(this.$appConfig.colors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--color-${key}`, value);
      document.documentElement.style.setProperty(`--${key}`, value);
      
      // Set RGB values for gradients
      const rgbValue = hexToRgb(value);
      if (rgbValue) {
        document.documentElement.style.setProperty(`--${key}-rgb`, rgbValue);
      }
    });

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");

    store.dispatch("auth/user");
  },
  created() {
    const localAppVersion = localStorage.getItem('app-version')?.split('.') ?? '';
    const localAppVersionObject = {
      major: Number(localAppVersion[0]),
      minor: Number(localAppVersion[1]),
      patch: Number(localAppVersion[2]),
    }

    const isShouldClearCache = this.appVersion.major > localAppVersionObject.major ||
      this.appVersion.minor > localAppVersionObject.minor

    if (isShouldClearCache) {
      alert("Versi aplikasi baru telah tersedia. Silahkan hapus cache dan refresh halaman.")
    } 

    localStorage.setItem('app-version', `${this.appVersion.major}.${this.appVersion.minor}.${this.appVersion.patch}`);
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    const isMaintenance = computed(() => !!Number(process.env.VUE_APP_IS_MAINTENANCE))

    return {
      skinClasses,
      isMaintenance
    };
  },
};
</script>
